import {
  ActivityIcon,
  ChartBarIcon,
  ChartLineIcon,
  ChartPieIcon,
  ClockIcon,
  EyeIcon,
  GlobeIcon,
  TrendUpIcon,
  UsersIcon,
} from '@phosphor-icons/react'
import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/_dashboard/analytics/')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <div className="min-h-screen">
      <div className="px-8 py-12">
        <div className="mx-auto max-w-7xl">
          {/* Animated Icon */}
          <div className="relative mb-8 flex justify-center mt-8">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full blur-2xl opacity-30 animate-pulse max-w-3xl mx-auto"></div>
            <div className="relative inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-2xl animate-bounce">
              <ChartLineIcon className="w-12 h-12 text-white animate-bounce" />
            </div>
          </div>

          {/* Header Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-6 shadow-lg">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              Coming Soon
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Analytics Dashboard
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Track your website's performance with comprehensive analytics and
              insights
            </p>
          </div>

          {/* Preview Cards Grid */}
          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {/* Traffic Overview Card */}
            <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <ChartLineIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">
                  Traffic Overview
                </h3>
              </div>

              {/* Mock Chart Area */}
              <div className="h-64 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl flex items-center justify-center mb-6">
                <div className="text-center">
                  <ChartBarIcon className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">
                    Interactive traffic charts will appear here
                  </p>
                </div>
              </div>

              {/* Mock Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">12.5K</div>
                  <div className="text-sm text-gray-500">Total Visitors</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-600">
                    +23%
                  </div>
                  <div className="text-sm text-gray-500">vs Last Month</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">2.4m</div>
                  <div className="text-sm text-gray-500">Page Views</div>
                </div>
              </div>
            </div>

            {/* Quick Stats Card */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 transition-all duration-300">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                  <ActivityIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Quick Stats
                </h3>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <EyeIcon className="w-5 h-5 text-blue-500" />
                    <span className="text-gray-600">Page Views</span>
                  </div>
                  <span className="font-semibold text-gray-900">2.4M</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <UsersIcon className="w-5 h-5 text-green-500" />
                    <span className="text-gray-600">Unique Visitors</span>
                  </div>
                  <span className="font-semibold text-gray-900">12.5K</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ClockIcon className="w-5 h-5 text-purple-500" />
                    <span className="text-gray-600">Avg. Session</span>
                  </div>
                  <span className="font-semibold text-gray-900">3:24</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <TrendUpIcon className="w-5 h-5 text-orange-500" />
                    <span className="text-gray-600">Bounce Rate</span>
                  </div>
                  <span className="font-semibold text-gray-900">42%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 transition-all duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <ChartPieIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Traffic Sources
                </h3>
              </div>
              <p className="text-gray-600">
                See where your visitors are coming from with detailed breakdowns
                of organic, direct, social, and referral traffic.
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 transition-all duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                  <GlobeIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Geographic Data
                </h3>
              </div>
              <p className="text-gray-600">
                Understand your audience with detailed geographic analytics
                showing visitor locations and regional performance.
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 transition-all duration-300">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex items-center justify-center">
                  <TrendUpIcon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Performance Trends
                </h3>
              </div>
              <p className="text-gray-600">
                Track your website's growth over time with comprehensive trend
                analysis and performance metrics.
              </p>
            </div>
          </div>

          {/* Coming Soon CTA */}
          <div className="text-center bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4">
              Analytics Dashboard Coming Soon
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Get ready to dive deep into your website's performance using
              Google Search Console data
            </p>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full text-sm font-semibold">
              <ClockIcon className="w-4 h-4" />
              Stay tuned for updates
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
